// Composer attachments (D36): early checks in the browser (count, size, type — the API checks again, bytes included),
// immediate upload of each accepted file, and the chips' state. Only ids travel with the question.
import { useCallback, useEffect, useRef, useState } from "react";
import { ASSISTANT_ATTACHMENTS_PER_MESSAGE, ASSISTANT_ATTACHMENT_MAX_BYTES, classifyAssistantAttachment, type AssistantAttachmentKind, type AssistantAttachmentRef } from "@maestros/contracts";
import { uploadAssistantAttachment } from "../../api/endpoints-composer.ts";
import type { Translate } from "../../i18n/index.tsx";
import { apiErrorView } from "../../lib/apiErrors.ts";
import { newId } from "../../lib/storage.ts";

export interface ComposerAttachmentView {
  key: string;
  name: string;
  size: number;
  mime: string;
  kind: AssistantAttachmentKind;
  state: "uploading" | "ready" | "error";
  /** The stored attachment id once uploaded. */
  id?: string;
  /** The same id for the chip's thumbnail (D36.4), and the local file it shows while uploading. */
  attachmentId?: string;
  file?: File;
  /** Translated reason of a failed upload (D36.4: never the server's English message). */
  error?: string;
}

export interface FileCheck {
  accepted: { file: File; kind: AssistantAttachmentKind; mime: string }[];
  rejections: string[];
}

/** Count (per message), size and type of the files being added to `existing` attachments. */
export function checkFiles(t: Translate, files: readonly File[], existing: number): FileCheck {
  const accepted: FileCheck["accepted"] = [];
  const rejections: string[] = [];
  for (const file of files) {
    const kind = classifyAssistantAttachment(file.name, file.type);
    if (!kind) rejections.push(t("composer.reject.type", { name: file.name }));
    else if (file.size === 0) rejections.push(t("composer.reject.empty", { name: file.name }));
    else if (file.size > ASSISTANT_ATTACHMENT_MAX_BYTES) rejections.push(t("composer.reject.size", { name: file.name }));
    else if (existing + accepted.length >= ASSISTANT_ATTACHMENTS_PER_MESSAGE) {
      const msg = t("composer.reject.count", { max: ASSISTANT_ATTACHMENTS_PER_MESSAGE });
      if (!rejections.includes(msg)) rejections.push(msg);
    } else accepted.push({ file, ...kind });
  }
  return { accepted, rejections };
}

/** The display facts the turn stores (and the optimistic bubble shows) for ready attachments. */
export function attachmentRefs(items: readonly ComposerAttachmentView[]): AssistantAttachmentRef[] {
  return items.flatMap((a) => (a.state === "ready" && a.id ? [{ id: a.id, name: a.name, size_bytes: a.size, mime: a.mime, kind: a.kind }] : []));
}

export type Uploader = typeof uploadAssistantAttachment;

/** Attachments of one composer, uploaded to `projectId`'s scope (null = Home). A scope change starts empty. */
export function useComposerAttachments(t: Translate, projectId: string | null, upload: Uploader = uploadAssistantAttachment) {
  const [items, setItems] = useState<ComposerAttachmentView[]>([]);
  const [rejections, setRejections] = useState<string[]>([]);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const controllers = useRef(new Map<string, AbortController>());

  const clear = useCallback(() => {
    for (const c of controllers.current.values()) c.abort();
    controllers.current.clear();
    itemsRef.current = [];
    setItems([]);
    setRejections([]);
  }, []);

  useEffect(() => clear, [clear, projectId]);

  const add = useCallback(
    (files: readonly File[]) => {
      const { accepted, rejections: refused } = checkFiles(t, files, itemsRef.current.length);
      setRejections(refused);
      if (accepted.length === 0) return;
      const fresh = accepted.map(({ file, kind, mime }): ComposerAttachmentView & { file: File } => ({ key: newId(), name: file.name, size: file.size, mime, kind, state: "uploading", file }));
      itemsRef.current = [...itemsRef.current, ...fresh];
      setItems(itemsRef.current);
      for (const item of fresh) {
        const controller = new AbortController();
        controllers.current.set(item.key, controller);
        upload(item.file, item.name, projectId, controller.signal)
          .then((stored) => {
            itemsRef.current = itemsRef.current.map((a) => (a.key === item.key ? { ...a, state: "ready", id: stored.id, attachmentId: stored.id, name: stored.name, mime: stored.mime, kind: stored.kind } : a));
            setItems(itemsRef.current);
          })
          .catch((e: unknown) => {
            if (controller.signal.aborted) return;
            const reason = apiErrorView(e, t).message;
            itemsRef.current = itemsRef.current.map((a) => (a.key === item.key ? { ...a, state: "error", error: reason } : a));
            setItems(itemsRef.current);
          })
          .finally(() => controllers.current.delete(item.key));
      }
    },
    [projectId, t, upload],
  );

  const remove = useCallback((key: string) => {
    controllers.current.get(key)?.abort();
    controllers.current.delete(key);
    itemsRef.current = itemsRef.current.filter((a) => a.key !== key);
    setItems(itemsRef.current);
  }, []);

  const uploading = items.some((a) => a.state === "uploading");
  const ids = items.flatMap((a) => (a.state === "ready" && a.id ? [a.id] : []));
  return { items, rejections, add, remove, clear, uploading, ids, dismissRejections: () => setRejections([]) };
}
